import { useState } from 'react';
import {
  submitUrl, submitUrls, crawlPreview, crawlQueue,
  type CrawlPreviewResult,
} from '../lib/api.js';

type Tab = 'single' | 'bulk' | 'crawl';

export default function AddView() {
  const [tab, setTab] = useState<Tab>('single');

  const [singleUrl, setSingleUrl] = useState('');
  const [singleStatus, setSingleStatus] = useState<string | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);

  const [bulkText, setBulkText] = useState('');
  const [bulkStatus, setBulkStatus] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawlMax, setCrawlMax] = useState(20);
  const [crawlData, setCrawlData] = useState<{ urls: CrawlPreviewResult[]; newCount: number; estimatedCost: string } | null>(null);
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [crawlQueued, setCrawlQueued] = useState<number | null>(null);

  async function handleSingleSubmit() {
    if (!singleUrl.trim()) return;
    setSingleLoading(true); setSingleStatus(null);
    try {
      const { queued } = await submitUrl(singleUrl.trim());
      setSingleStatus(queued > 0 ? '✓ Queued for processing (~30s)' : '⟳ Already in your collection');
      if (queued > 0) setSingleUrl('');
    } catch {
      setSingleStatus('✗ Failed to submit — check the URL and try again');
    } finally { setSingleLoading(false); }
  }

  async function handleBulkSubmit() {
    const urls = bulkText.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 100);
    if (!urls.length) return;
    setBulkLoading(true); setBulkStatus(null);
    try {
      const { queued, skipped } = await submitUrls(urls);
      setBulkStatus(`✓ ${queued} queued, ${skipped} already in collection`);
      if (queued > 0) setBulkText('');
    } catch { setBulkStatus('✗ Failed to submit'); }
    finally { setBulkLoading(false); }
  }

  async function handleCrawlPreview() {
    if (!crawlUrl.trim()) return;
    setCrawlLoading(true); setCrawlData(null); setCrawlQueued(null);
    try {
      setCrawlData(await crawlPreview(crawlUrl.trim(), crawlMax));
    } catch (err) {
      alert(`Preview failed: ${(err as Error).message}`);
    } finally { setCrawlLoading(false); }
  }

  async function handleCrawlQueue() {
    if (!crawlData) return;
    const urls = crawlData.urls.filter((u) => !u.alreadyIngested).map((u) => u.url);
    setCrawlLoading(true);
    try {
      const { queued } = await crawlQueue(urls);
      setCrawlQueued(queued); setCrawlData(null);
    } catch { alert('Failed to queue URLs'); }
    finally { setCrawlLoading(false); }
  }

  const bulkCount = bulkText.split('\n').filter((l) => l.trim()).length;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-xl font-semibold text-gray-100 mb-6">Add URLs</h1>

      <div className="flex border-b border-gray-700 mb-6">
        {(['single', 'bulk', 'crawl'] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2 text-sm border-b-2 transition-colors ${tab === t ? 'border-obsidian-accent text-obsidian-accent' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
            {t === 'crawl' ? '🕷 Crawl a site' : t === 'bulk' ? 'Bulk paste' : 'Single URL'}
          </button>
        ))}
      </div>

      {tab === 'single' && (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 block mb-2">URL</label>
            <div className="flex gap-2">
              <input type="url" value={singleUrl} onChange={(e) => setSingleUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleSingleSubmit()}
                placeholder="https://docs.anthropic.com/..." className="input flex-1" />
              <button onClick={() => void handleSingleSubmit()} disabled={!singleUrl.trim() || singleLoading}
                className="btn-primary px-4 disabled:opacity-50">
                {singleLoading ? '...' : 'Process →'}
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-1">Supports articles, YouTube, GitHub repos, Reddit, X/Twitter</p>
          </div>
          {singleStatus && (
            <div className={`card p-3 text-sm ${singleStatus.startsWith('✓') ? 'border-green-800 text-green-400' : singleStatus.startsWith('⟳') ? 'border-gray-600 text-gray-400' : 'border-red-800 text-red-400'}`}>
              {singleStatus}
            </div>
          )}
        </div>
      )}

      {tab === 'bulk' && (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 block mb-2">One URL per line (max 100)</label>
            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)}
              placeholder={"https://example.com/article-1\nhttps://example.com/article-2"}
              rows={8} className="input w-full font-mono text-sm resize-y" />
            <p className="text-xs text-gray-600 mt-1">{bulkCount} URL{bulkCount !== 1 ? 's' : ''} detected</p>
          </div>
          <button onClick={() => void handleBulkSubmit()} disabled={bulkCount === 0 || bulkLoading}
            className="btn-primary w-full disabled:opacity-50">
            {bulkLoading ? 'Submitting...' : `Queue ${bulkCount} URL${bulkCount !== 1 ? 's' : ''}`}
          </button>
          {bulkStatus && <div className="card p-3 text-sm text-green-400 border-green-800">{bulkStatus}</div>}
        </div>
      )}

      {tab === 'crawl' && (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 block mb-2">Starting URL</label>
            <input type="url" value={crawlUrl} onChange={(e) => setCrawlUrl(e.target.value)}
              placeholder="https://docs.anthropic.com/en/docs/claude-code" className="input w-full" />
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-2">Max pages: {crawlMax}</label>
              <input type="range" min={5} max={50} value={crawlMax}
                onChange={(e) => setCrawlMax(Number(e.target.value))} className="w-full" />
              <div className="flex justify-between text-xs text-gray-600 mt-1"><span>5</span><span>50</span></div>
            </div>
            <div className="flex-1 card p-3 text-sm">
              <div className="text-gray-500 text-xs mb-1">Same domain only · depth 1</div>
              <div className="text-yellow-400 text-xs">Est. cost: ~${(crawlMax * 0.002).toFixed(2)}</div>
            </div>
          </div>
          <button onClick={() => void handleCrawlPreview()} disabled={!crawlUrl.trim() || crawlLoading}
            className="btn-primary w-full disabled:opacity-50">
            {crawlLoading && !crawlData ? 'Scanning...' : 'Preview URLs →'}
          </button>

          {crawlQueued !== null && (
            <div className="card p-3 text-sm text-green-400 border-green-800">✓ {crawlQueued} URLs queued for processing</div>
          )}

          {crawlData && (
            <div className="space-y-3">
              <div className="card p-3 text-sm">
                <div className="text-gray-300 mb-1">
                  {crawlData.newCount} new · {crawlData.urls.length - crawlData.newCount} already in collection
                </div>
                <div className="text-yellow-400 text-xs">Estimated cost: ~${crawlData.estimatedCost}</div>
              </div>
              <div className="card p-3 max-h-48 overflow-y-auto font-mono text-xs space-y-1">
                {crawlData.urls.map((u) => (
                  <div key={u.url} className={u.alreadyIngested ? 'text-gray-600' : 'text-gray-300'}>
                    {u.alreadyIngested ? '✓ ' : '• '}{u.url.length > 70 ? '…' + u.url.slice(-67) : u.url}
                  </div>
                ))}
              </div>
              <button onClick={() => void handleCrawlQueue()} disabled={crawlData.newCount === 0 || crawlLoading}
                className="btn-primary w-full disabled:opacity-50">
                Queue {crawlData.newCount} new pages
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
